import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { useSizing, modelForProduct } from "@/lib/sizing-store";
import { Input } from "@/components/ui/input";
import { PrimaryButton } from "@/components/design-system";
import { AssignOptionRow } from "../dialogs/AssignmentDialogShell";
import { BulkAssignmentShell } from "./BulkAssignmentShell";

/**
 * BulkModelDialog — assign a Model + a SHARED "worn size" across many
 * selected products at once. Two-step interaction inside the dialog:
 *
 *   1. Merchant picks a model row (AssignOptionRow with initials avatar +
 *      height). Selection stays highlighted; the picker doesn't close.
 *   2. A small "giydiği beden" input appears at the bottom + a "Uygula"
 *      button that fires the actual mutation.
 *
 * Rationale for single shared size: bulk-assigning a model typically means
 * "this model is our fit reference for these products", and it's the same
 * fit across product variants. If per-product sizes are needed, merchants
 * can go one-by-one via the single-product dialog.
 */
export const BulkModelDialog = ({ selectedProducts, onClose }) => {
    const { models, products, setProductModel } = useSizing();
    const [query, setQuery] = useState("");
    const [pickedModelId, setPickedModelId] = useState(null);
    const [wornSize, setWornSize] = useState("");
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("empty-only");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return models;
        return models.filter((m) => (m.name || "").toLowerCase().includes(q));
    }, [models, query]);

    const conflicts = selectedProducts.filter((p) => !!p.modelId);
    const conflictCount = conflicts.length;

    const apply = async () => {
        if (saving) return;
        if (!pickedModelId) {
            toast.info("Önce bir model seç");
            return;
        }
        if (!wornSize.trim()) {
            toast.error("Modelin giydiği bedeni yaz");
            return;
        }
        const targets = mode === "empty-only"
            ? selectedProducts.filter((p) => !p.modelId)
            : selectedProducts;
        if (targets.length === 0) {
            toast.info("Etkilenecek ürün yok");
            return;
        }
        setSaving(true);
        try {
            // Snapshot the current model+size per target so the Geri Al toast
            // can restore each product to its previous state (including "no
            // assignment" for products that had none).
            const snapshot = targets.map((p) => {
                const current = modelForProduct(models, p.id, products);
                return {
                    id: p.id,
                    prevModelId: current?.model.id || null,
                    prevSize: current?.size || null,
                };
            });
            targets.forEach((p) => setProductModel(p.id, pickedModelId, wornSize.trim()));
            const modelName = models.find((m) => m.id === pickedModelId)?.name || "Model";
            toast.success(`${targets.length} ürüne "${modelName} · ${wornSize.trim()}" atandı`, {
                duration: 5000,
                action: {
                    label: "Geri Al",
                    onClick: () => {
                        snapshot.forEach((s) => setProductModel(s.id, s.prevModelId, s.prevSize));
                        toast.success(`${snapshot.length} ürünün modeli eski haline döndü`);
                    },
                },
            });
            onClose();
        } catch {
            toast.error("Toplu model güncellemesi başarısız");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BulkAssignmentShell
            title="Toplu model ata"
            count={selectedProducts.length}
            conflictCount={conflictCount}
            mode={mode}
            onModeChange={setMode}
            onClose={onClose}
            testId="bulk-model-dialog"
        >
            {models.length > 0 && (
                <div className="sticky top-0 -mt-4 pt-3 pb-2 -mx-5 px-5 bg-muted z-10">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Model ara…"
                            className="h-9 pl-9 text-xs bg-background"
                            data-testid="bulk-model-dialog-search"
                        />
                    </div>
                </div>
            )}
            {models.length === 0 ? (
                <div className="text-center py-10 text-xs text-muted-foreground">Henüz model yok.</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-xs text-muted-foreground">Sonuç yok</div>
            ) : (
                filtered.map((m) => {
                    const initials = m.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                    return (
                        <AssignOptionRow
                            key={m.id}
                            current={pickedModelId === m.id}
                            onClick={() => setPickedModelId(m.id)}
                            disabled={saving}
                            testId={`bulk-model-option-${m.id}`}
                        >
                            <span className="h-10 w-10 rounded-md bg-foreground text-background grid place-items-center font-display font-bold text-xs shrink-0">
                                {initials}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">{m.name}</div>
                                <div className="text-[10.5px] text-muted-foreground truncate">{m.height} cm</div>
                            </div>
                        </AssignOptionRow>
                    );
                })
            )}
            {pickedModelId && (
                <div className="sticky bottom-0 -mb-4 -mx-5 mt-3 px-5 pt-3 pb-4 bg-card border-t border-border" data-testid="bulk-model-size-row">
                    <div className="text-[10.5px] text-muted-foreground mb-1.5">Modelin bu ürünlerde giydiği beden</div>
                    <div className="flex items-center gap-2">
                        <Input
                            value={wornSize}
                            onChange={(e) => setWornSize(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
                            placeholder="ör. M, 38, 32…"
                            className="h-9 text-sm bg-background flex-1"
                            autoFocus
                            data-testid="bulk-model-size-input"
                        />
                        <PrimaryButton
                            onClick={apply}
                            disabled={saving || !wornSize.trim()}
                            data-testid="bulk-model-apply"
                            className="h-9 text-xs px-4"
                        >
                            Uygula
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </BulkAssignmentShell>
    );
};
